import '@/polyfills/promiseWithResolvers'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import pdfWorkerUrl from '@/workers/pdf.worker?worker&url'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

type ContractPdfViewerProps = {
  file: Blob | string
  onLoadError?: () => void
  /** Acessibilidade; se omitido, usa o texto padrão do namespace onboarding */
  documentTitle?: string
}

export function ContractPdfViewer({
  file,
  onLoadError,
  documentTitle,
}: ContractPdfViewerProps) {
  const { t } = useTranslation('onboarding')
  const a11yTitle = documentTitle ?? t('contract_pdf_a11y_contract')
  const containerRef = useRef<HTMLDivElement>(null)
  const [numPages, setNumPages] = useState(0)
  const [pageWidth, setPageWidth] = useState(720)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const update = () => {
      // clientWidth já inclui o padding horizontal (px-2) do container
      const available = el.clientWidth - 16
      if (available > 0) setPageWidth(Math.min(available, 920))
    }

    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <div
      ref={containerRef}
      role="region"
      aria-label={a11yTitle}
      className="max-h-[70vh] overflow-y-auto overflow-x-hidden bg-zinc-950 py-4 px-2"
    >
      <Document
        file={file}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        onLoadError={() => {
          onLoadError?.()
        }}
        loading={
          <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
            {t('contract_pdf_loading')}
          </div>
        }
        error={
          <div className="flex min-h-[40vh] items-center justify-center px-4 text-center text-sm text-destructive">
            {t('contract_pdf_parse_error')}
          </div>
        }
      >
        {numPages > 0
          ? Array.from({ length: numPages }, (_, i) => (
              <Page
                key={i + 1}
                pageNumber={i + 1}
                width={pageWidth}
                className="mx-auto mb-4 shadow-lg"
                renderTextLayer
                renderAnnotationLayer
              />
            ))
          : null}
      </Document>
    </div>
  )
}
